// Everything that differs between the DB Services and Mission in Motion skins of
// the portal, apart from colors (those live as CSS custom properties in
// src/assets/css/index.css, selected by the data-brand attribute on <html>).
//
// Both brands are served from the same Amplify branch on different hostnames, so
// VUE_APP_* build-time variables cannot tell them apart. The brand is resolved at
// runtime instead, once, at app start. See src/utilities/brand.ts.

import dbCentralLogo from "@/assets/dbcentral.png"
import dbCentralLogoWhite from "@/assets/dbcentral-white.png"
import dbCentralLogoWhiteAbbreviated from "@/assets/dbcentral-white-abbreviated.png"
import dbServicesLogo from "@/assets/dbLogo.png"
import dbCardHeaderBg from "@/assets/card-header-bg.png"

import missionInMotionLogo from "@/assets/mission-in-motion-logo.svg"
import missionInMotionLogoWhite from "@/assets/mission-in-motion-logo-white.svg"
import missionInMotionMarkWhite from "@/assets/mission-in-motion-mark-white.svg"
import mimCardHeaderBg from "@/assets/card-header-bg-mim.png"

export type BrandId = "db" | "mim"

export interface BrandLogo {
	src: string
	alt: string
	// Sizing classes only. The two lockups have different aspect ratios, so each
	// brand needs its own. Positioning stays in the template.
	class: string
}

export interface Brand {
	id: BrandId
	name: string
	title: string
	metaDescription: string
	canonicalUrl: string
	faviconUrl: string
	siteUrl: string
	blogUrl: string
	// Stripe Elements takes a hex string in its appearance object, not a CSS class.
	stripeColorPrimary: string
	cardHeaderBg: string
	logos: {
		loginCard: BrandLogo
		// Omitted when the brand has a single lockup, so the login card does not
		// show the same logo twice. DB Services has two (dbcentral, then the
		// company logo); Mission in Motion has one.
		loginFooter?: BrandLogo
		sidebar: BrandLogo
		sidebarCollapsed: BrandLogo
	}
}

export const DEFAULT_BRAND_ID: BrandId = "db"

const MIM_HOSTNAME_MATCH = "missioninmotion.com"

export const BRANDS: Record<BrandId, Brand> = {
	db: {
		id: "db",
		name: "DB Services",
		title: "DB Central - DB Services Customer Portal",
		metaDescription: "Get access to our customer portal, DB Central, where you can easily access download links to FileMaker software, recent invoices, payments, W-9s, and more.",
		canonicalUrl: "https://customer.dbservices.com/",
		faviconUrl: "https://dbservices.com/assets/favicon-32x32.png",
		siteUrl: "https://www.dbservices.com",
		blogUrl: "https://dbservices.com/blog",
		stripeColorPrimary: "#3830a3",
		cardHeaderBg: dbCardHeaderBg,
		logos: {
			loginCard: {
				src: dbCentralLogo,
				alt: "DB Central",
				class: "mx-auto w-2/5 xl:h-full xl:w-full",
			},
			loginFooter: {
				src: dbServicesLogo,
				alt: "DB Services",
				class: "",
			},
			sidebar: {
				src: dbCentralLogoWhite,
				alt: "DB Central",
				class: "h-12 w-auto",
			},
			sidebarCollapsed: {
				src: dbCentralLogoWhiteAbbreviated,
				alt: "DB Central",
				class: "h-7 w-auto",
			},
		},
	},
	mim: {
		id: "mim",
		name: "Mission in Motion",
		title: "DB Central - Mission in Motion Customer Portal",
		metaDescription: "Get access to our customer portal, DB Central, where you can easily access recent invoices, payments, W-9s, and more.",
		canonicalUrl: "https://customer.missioninmotion.com/",
		faviconUrl: "https://missioninmotion.com/assets/favicons/favicon-32x32.png",
		siteUrl: "https://missioninmotion.com",
		blogUrl: "https://missioninmotion.com/blog",
		stripeColorPrimary: "#297fc3",
		cardHeaderBg: mimCardHeaderBg,
		logos: {
			loginCard: {
				src: missionInMotionLogo,
				alt: "Mission in Motion",
				class: "mx-auto w-3/5 xl:w-full",
			},
			sidebar: {
				src: missionInMotionLogoWhite,
				alt: "Mission in Motion",
				class: "h-11 w-auto",
			},
			sidebarCollapsed: {
				// The mark is landscape where the DB Central abbreviation is portrait,
				// so it borrows the sidebar's horizontal padding to stay legible.
				src: missionInMotionMarkWhite,
				alt: "Mission in Motion",
				class: "w-11 max-w-none -mx-2.5",
			},
		},
	},
}

// Returns whether a hostname is the Mission in Motion domain or one of its
// subdomains. The leading dot matters: a bare endsWith would also match
// lookalike domains such as notmissioninmotion.com.
function isMimHostname(hostname: string): boolean {
	return hostname === MIM_HOSTNAME_MATCH
		|| hostname.endsWith(`.${MIM_HOSTNAME_MATCH}`)
}

// Returns whether a hostname is one of the live portals, read from each brand's
// canonical URL so the list cannot drift from the domains the brands declare.
function isProductionHostname(hostname: string): boolean {
	return Object.values(BRANDS)
		.some((brand) => new URL(brand.canonicalUrl).hostname === hostname)
}

// Returns the brand named by an explicit override, or null when none applies.
// VUE_APP_BRAND pins a whole build to one skin. The ?brand= param flips between
// them without a rebuild, and is ignored on the live portals so that a crafted
// link cannot dictate which brand a visitor sees. It is keyed on the hostname
// rather than NODE_ENV, because staging and Amplify preview builds are production
// builds too, and they are where both skins need checking before release.
function overrideBrandId(): string | null {
	const fromQuery = isProductionHostname(window.location.hostname)
		? null
		: new URLSearchParams(window.location.search).get("brand")

	return fromQuery || process.env.VUE_APP_BRAND || null
}

// Returns the brand id for this page: an explicit override if one applies,
// otherwise the brand implied by the hostname.
export function resolveBrandId(): BrandId {
	const requested = overrideBrandId()

	// hasOwn rather than "in": "in" also finds inherited keys such as toString, so
	// ?brand=toString would resolve to a function and blank the app at boot
	if (requested && Object.hasOwn(BRANDS, requested)) return requested as BrandId

	if (isMimHostname(window.location.hostname)) return "mim"

	return DEFAULT_BRAND_ID
}
